import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { businessesAPI, appConfigAPI } from '../services/api';
import billingApi from '../services/billingApi';
import { invitesAPI } from '../features/businesses/services/invitesApi';
import adminApi from '../services/adminApi';
import { useAuth } from '../contexts/AuthContext';
import { activeSwitcherLocations, switcherSelection } from '../utils/locationSwitcher';
import BusinessMultiSelect, { useBusinessMultiKey } from './BusinessMultiSelect';
import SelectedBusinessesLabel from './SelectedBusinessesLabel';
import { ALL, readSavedBusiness, saveBusiness } from '../utils/businessSelection';
import PageGuide from './PageGuide';
import { formatApiError } from '../utils/errorMessage';
import { formationLockState, graceMessage } from '../utils/formationLock';
import { SetupQuestions } from '../features/businessSetup/SetupChecklist';
import { assetUrl, apiOrigin } from '../utils/assetUrl';
import { allowedPagesFor } from '../utils/rolePages';

// Pages a role can be limited from (every app page but Dashboard, Settings
// and Bank Feeds, which follows its own delegated permission).
const ROLE_GATED_PATHS = new Set(['/transactions', '/categories', '/budgets', '/accounts', '/subscriptions', '/recurring-bills',
  '/business', '/vehicles', '/properties', '/assets', '/asset-expenses', '/creditors', '/customers', '/inventory', '/quotes',
  '/accounts-receivable', '/accounts-payable', '/staff', '/payroll', '/schedules', '/reconciliation', '/tasks',
  '/resolution-letters', '/reports', '/billing']);

const DIVISION_SCOPED_HIDDEN_PATHS = new Set([
  '/accounts', '/subscriptions', '/recurring-bills',
  '/vehicles', '/properties', '/assets', '/asset-expenses', '/creditors',
  '/customers', '/inventory', '/quotes', '/accounts-receivable', '/accounts-payable',
  '/staff', '/payroll', '/schedules', '/reconciliation', '/bank-feeds', '/resolution-letters',
  '/reports', '/billing', '/business'
]);

// CSS animations for logo
const logoAnimationStyles = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  @keyframes spinHorizontal {
    from {
      transform: rotateY(0deg);
    }
    to {
      transform: rotateY(360deg);
    }
  }
  
  .logo-spin-horizontal {
    animation: spinHorizontal 8s linear infinite;
    transform-style: preserve-3d;
  }
  .sidebar-nav-scroll {
    scrollbar-width: none;
  }
  .sidebar-nav-scroll::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth(); const multiKey = useBusinessMultiKey();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const closeMenu = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [accountMenuOpen]);
  const [businesses, setBusinesses] = useState([]);
  const [locationsError, setLocationsError] = useState(false);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  // Persist selected business across reloads, navigation and tabs
  const [selectedBusiness, setSelectedBusiness] = useState(() => {
    const saved = readSavedBusiness();
    if (saved === ALL) return '';
    return saved || null;
  });
  const [serverStatus, setServerStatus] = useState('checking'); // 'online' | 'offline' | 'checking'
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  // '' until the person picks Personal or Business in the add-location form.
  const [locationKind, setLocationKind] = useState('');
  // Location just created as a formed business: offer the optional document step.
  const [documentsStep, setDocumentsStep] = useState(null);
  // Business location just created: ask the setup questions first (skippable).
  const [checklistStep, setChecklistStep] = useState(null);
  const [documentsSaving, setDocumentsSaving] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [businessFormData, setBusinessFormData] = useState({
    name: '', legal_name: '', tax_id: '', business_type: 'LLC', description: '', parent_business_id: ''
  });
  const [appConfig, setAppConfig] = useState(null);
  const [selectedBusinessLogo, setSelectedBusinessLogo] = useState(null);
  const [divisionScopesByBusiness, setDivisionScopesByBusiness] = useState({});

  useEffect(() => {
    loadBusinesses();
    loadAppConfig();
    loadDivisionScopes();
    // Local-dev-only convenience: checks/restarts the local backend process.
    // Hardcoded to localhost, so it's meaningless (and noisy) against a real
    // hosted deployment.
    if (process.env.NODE_ENV !== 'production') {
      checkServerStatus();
      const i = setInterval(checkServerStatus, 30000);
      return () => clearInterval(i);
    }
  }, []);

  // Keep sidebar business list in sync when Business page creates/updates/deletes
  useEffect(() => {
    const onBusinessesUpdated = async (event) => {
      const createdId = event?.detail?.businessId;
      const deletedId = event?.detail?.deletedBusinessId;
      await loadBusinesses();
      await loadDivisionScopes();
      if (createdId && Number(createdId) !== Number(deletedId)) {
        setSelectedBusiness(Number(createdId));
      }
    };
    window.addEventListener('businesses-updated', onBusinessesUpdated);
    return () => window.removeEventListener('businesses-updated', onBusinessesUpdated);
  }, []);

  const loadAppConfig = async () => {
    try {
      const res = await appConfigAPI.get();
      const config = res.data || {};
      setAppConfig(config);
      
      // Apply custom CSS if provided
      if (config.custom_css) {
        const styleId = 'custom-app-css';
        let styleElement = document.getElementById(styleId);
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = styleId;
          document.head.appendChild(styleElement);
        }
        styleElement.textContent = config.custom_css;
      }
      
      // Apply favicon if provided, otherwise use default eloNest symbol
      const faviconHref = assetUrl(config.favicon_url);
      if (faviconHref) {
        const favicon = document.querySelector("link[rel='icon']") || document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = faviconHref;
        if (!document.querySelector("link[rel='icon']")) {
          document.head.appendChild(favicon);
        }
      } else {
        // Set default eloNest favicon if no custom favicon
        const favicon = document.querySelector("link[rel='icon']");
        if (favicon) {
          favicon.href = '/img/eloNestsymbol.png';
        }
      }
      
      // Apply CSS variables for colors
      if (config.primary_color || config.secondary_color || config.accent_color) {
        const root = document.documentElement;
        if (config.primary_color) {
          root.style.setProperty('--primary-color', config.primary_color);
        }
        if (config.secondary_color) {
          root.style.setProperty('--secondary-color', config.secondary_color);
        }
        if (config.accent_color) {
          root.style.setProperty('--accent-color', config.accent_color);
        }
      }
    } catch (e) {
      console.error('Failed to load app config:', e?.response?.status || e?.code || 'no response', e?.response?.data?.code || '');
      // Set defaults
      setAppConfig({
        app_name: 'eloNest',
        primary_color: '#3B82F6',
        secondary_color: '#10B981',
        accent_color: '#F59E0B',
      });
    }
  };

  const checkServerStatus = async () => {
    try {
      const res = await axios.get(`${apiOrigin()}/`, { timeout: 5000 });
      setServerStatus(res.status === 200 ? 'online' : 'offline');
    } catch {
      setServerStatus('offline');
    }
  };

  const requestRestart = async () => {
    try {
      setServerStatus('checking');
      await adminApi.restartServer('backend');
      // Poll for server to come back
      setTimeout(checkServerStatus, 2000);
      setTimeout(checkServerStatus, 4000);
      setTimeout(checkServerStatus, 7000);
      alert('Server restart requested. It should be back in a few seconds.');
    } catch (e) {
      alert('Failed to request restart (are you on localhost/dev and logged in as admin/superuser?).');
    }
  };

  const loadBusinesses = async () => {
    try {
      const res = await businessesAPI.getAll();
      const list = Array.isArray(res.data) ? res.data : [];
      setLocationsError(false);
      setLocationsLoaded(true);
      setBusinesses(list);
      const active = activeSwitcherLocations(list);
      const savedId = readSavedBusiness();
      const next = switcherSelection(selectedBusiness, savedId, active);
      const currentNum = selectedBusiness ? Number(selectedBusiness) : '';
      if (String(next) !== String(currentNum)) {
        setSelectedBusiness(next === '' ? '' : next);
      }
      return list;
    } catch (e) {
      console.error('Failed to load businesses', e?.response?.status || e?.code || 'no response', e?.response?.data?.code || '');
      setLocationsError(true);
      setLocationsLoaded(true);
      setBusinesses([]);
      return [];
    }
  };

  // Save selection changes ('' means the user chose All Businesses;
  // null means nothing chosen yet, so nothing is saved).
  useEffect(() => {
    if (selectedBusiness === null) return;
    saveBusiness(selectedBusiness);
  }, [selectedBusiness]);

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    try {
      const personal = locationKind === 'personal';
      const payload = {
        ...businessFormData,
        location_type: personal ? 'personal' : 'business',
        legal_structure: personal ? 'Personal' : businessFormData.business_type,
        legal_name: personal ? '' : businessFormData.legal_name,
        tax_id: personal ? '' : businessFormData.tax_id,
        parent_business_id: businessFormData.parent_business_id
          ? Number(businessFormData.parent_business_id)
          : null
      };
      const res = await businessesAPI.create(payload);
      const createdLocation = res?.data || null;
      if (createdLocation && !personal) {
        setChecklistStep(createdLocation);
        setDocumentsError('');
      } else if (createdLocation && formationLockState(createdLocation).formed) {
        setDocumentsStep(createdLocation);
        setDocumentsError('');
      } else {
        setShowBusinessModal(false);
        setLocationKind('');
      }
      setBusinessFormData({
        name: '', legal_name: '', tax_id: '', business_type: 'LLC', description: '', parent_business_id: ''
      });
      await loadBusinesses();
      const createdId = res?.data?.id;
      if (createdId) {
        setSelectedBusiness(Number(createdId));
      }
      try {
        window.dispatchEvent(new CustomEvent('businesses-updated', { detail: { businessId: createdId } }));
      } catch {}
    } catch (err) {
      alert(formatApiError(err, 'Failed to create business'));
    }
  };

  const getBusinessOptionLabel = (business) => {
    const label = business.name || business.legal_name || `Business ${business.id}`;
    return business.parent_business_id ? `↳ ${label}` : label;
  };

  const loadDivisionScopes = async () => {
    try {
      const res = await invitesAPI.myScopes();
      setDivisionScopesByBusiness(res.data?.divisionScopesByBusiness || {});
    } catch {
      setDivisionScopesByBusiness({});
    }
  };

  const selectedDivisionScope = selectedBusiness
    ? (divisionScopesByBusiness[selectedBusiness] ?? divisionScopesByBusiness[String(selectedBusiness)])
    : null;
  const isDivisionScopedUser = Array.isArray(selectedDivisionScope);
  const selectedLocation = businesses.find((biz) => biz.id === Number(selectedBusiness));
  const switcherLocations = activeSwitcherLocations(businesses);
  const isPersonalLocation = selectedLocation?.legal_structure === 'Personal';
  const personalHiddenPaths = new Set(['/payroll', '/accounts-receivable', '/resolution-letters', '/customers', '/inventory', '/quotes']);
  const formationLock = formationLockState(selectedLocation);
  const formedWaiting = formationLock.locked;
  const formedClosedPaths = new Set(['/payroll', '/accounts-receivable', '/resolution-letters']);
  const callerRole = selectedLocation?.caller_role || (user?.is_superuser ? 'owner' : null);
  const leaderRole = callerRole === 'owner' || callerRole === 'admin' || user?.is_superuser;
  // Role pages come from the server (allowed_pages on each location; the same
  // rule the API enforces). null = no limit.
  const allowedPages = user?.is_superuser ? null : allowedPagesFor(businesses, selectedBusiness);
  // A page the role cannot open is not rendered (and its data not requested).
  const pageBlocked = Boolean(allowedPages) && ROLE_GATED_PATHS.has('/' + (location.pathname.split('/')[1] || '')) && !allowedPages.has('/' + (location.pathname.split('/')[1] || ''));

  const navigationSections = [
    { title: 'Overview', items: [
      { name: 'Dashboard', path: '/dashboard', icon: '📊' }
    ]},
    { title: 'Finance', items: [
      { name: 'Transactions', path: '/transactions', icon: '💰' },
      { name: 'Categories', path: '/categories', icon: '🏷️' },
      { name: 'Budgets', path: '/budgets', icon: '📈' },
      
      { name: 'Accounts', path: '/accounts', icon: '🏦' },
      { name: 'Subscriptions', path: '/subscriptions', icon: '🔔' },
      { name: 'Recurring Bills', path: '/recurring-bills', icon: '📄' },
    ]},
    { title: 'Assets & Properties', items: [
      { name: 'Business', path: '/business', icon: '🏢' },
      { name: 'Vehicles', path: '/vehicles', icon: '🚗' },
      { name: 'Properties', path: '/properties', icon: '🏠' },
      { name: 'Assets', path: '/assets', icon: '💼' },
      { name: 'Asset Expenses', path: '/asset-expenses', icon: '💸' },
      { name: 'Creditors', path: '/creditors', icon: '📋' },
    ]},
    { title: 'Clients & Services', items: [
      { name: 'Customers', path: '/customers', icon: '👥' },
      { name: 'Products', path: '/inventory', icon: '🏷️' },
      { name: 'Quotes', path: '/quotes', icon: '📝' },
      { name: 'Accounts Receivable', path: '/accounts-receivable', icon: '💵' },
      { name: 'Accounts Payable', path: '/accounts-payable', icon: '📋' },
    ]},
    { title: 'Operations', items: [
      { name: 'Staff', path: '/staff', icon: '👤' },
      { name: 'Payroll', path: '/payroll', icon: '💳' },
      { name: 'Schedules', path: '/schedules', icon: '📅' },
      { name: 'Reconciliation', path: '/reconciliation', icon: '🔄' },
      { name: 'Bank Feeds', path: '/bank-feeds', icon: '🏦' },
      { name: 'Tasks', path: '/tasks', icon: '✅' },
      { name: 'Resolution Letters', path: '/resolution-letters', icon: '📜' },
    ]},
    { title: 'Reports', items: [{ name: 'Reports', path: '/reports', icon: '📋' }]},
    // Admin appears after Reports
    ...(user && (user.is_superuser || user.role === 'admin') ? [
      { title: 'Admin', items: [
        ...(user && (user.is_superuser || user.role === 'superuser') ? [
          { name: 'SaaS Dashboard', path: '/admin/dashboard', icon: '📊' },
          { name: 'Security', path: '/admin/security', icon: '🛡️' },
          { name: 'Error reports', path: '/admin/error-reports', icon: '⚠️' },
          { name: 'Support messages', path: '/admin/support-messages', icon: '💬' },
        ] : []),
        { name: 'Users', path: '/admin/users', icon: '👥' },
      ]}
    ] : []),
  ].map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      // Bank Feeds follows the delegated bank_feeds.view permission, not the role.
      if (item.path === '/bank-feeds' && selectedLocation) return Boolean(selectedLocation.bank_feeds_view) && !(isDivisionScopedUser && !leaderRole);
      if (item.path !== '/settings' && allowedPages && !allowedPages.has(item.path)) return false;
      if (isPersonalLocation && personalHiddenPaths.has(item.path)) return false;
      if (formedWaiting && formedClosedPaths.has(item.path)) return false;
      return true;
    })
  })).filter((section) => section.items.length > 0);

  const appName = (appConfig?.app_name && appConfig.app_name !== 'Financial Planner') ? appConfig.app_name : 'eloNest';
  const accountPageTitles = { '/billing': 'eloNest plan', '/settings': 'Settings' };
  const currentTitle = accountPageTitles[location.pathname] || navigationSections.flatMap(s => s.items).find(i => i.path === location.pathname)?.name || appName;
  const accountName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Account';
  const accountInitial = (user?.first_name || user?.email || 'A').charAt(0).toUpperCase();
  
  // Quick access menu items
  const quickMenuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: '📊' },
    { name: 'Transactions', path: '/transactions', icon: '💰' },
    { name: 'Budgets', path: '/budgets', icon: '📈' },
    { name: 'Reports', path: '/reports', icon: '📋' },
  ];

  const getSelectedBusinessName = () => {
    if (!locationsLoaded) return 'Loading…';
    if (!selectedBusiness) return 'All Businesses';
    const b = businesses.find(biz => biz.id === Number(selectedBusiness));
    if (!b) return 'All Businesses';
    if (b.parent_business_id) {
      const parent = businesses.find(biz => biz.id === Number(b.parent_business_id));
      const parentName = parent?.name || b.parent_business_name;
      return parentName ? `${b.name} (under ${parentName})` : b.name;
    }
    return b.name || 'All Businesses';
  };

  const refreshSelectedBusinessLogo = useCallback(async () => {
    if (!selectedBusiness) {
      setSelectedBusinessLogo(null);
      return;
    }
    try {
      const res = await businessesAPI.getBranding(selectedBusiness);
      setSelectedBusinessLogo(assetUrl(res.data?.logo_url));
    } catch {
      setSelectedBusinessLogo(null);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    refreshSelectedBusinessLogo();
  }, [refreshSelectedBusinessLogo]);

  // A location without a trialing or paid plan shows only the plan prompt.
  const [planLock, setPlanLock] = useState(null);
  useEffect(() => {
    setPlanLock(null);
    if (!selectedBusiness || user?.is_superuser) return undefined;
    let current = true;
    const checkPlan = () => {
      billingApi.getSubscription(selectedBusiness)
        .then((res) => {
          if (!current || !res.data?.access) return;
          setPlanLock(res.data.access.open ? null : (res.data.access.reason || 'no_subscription'));
        })
        .catch(() => {});
    };
    checkPlan();
    window.addEventListener('billing-updated', checkPlan);
    return () => {
      current = false;
      window.removeEventListener('billing-updated', checkPlan);
    };
  }, [selectedBusiness, user?.is_superuser]);

  useEffect(() => {
    const handler = (event) => {
      const lockedId = Number(event.detail?.businessId);
      if (!lockedId || lockedId === Number(selectedBusiness)) setPlanLock('no_subscription');
    };
    window.addEventListener('subscription-required', handler);
    return () => window.removeEventListener('subscription-required', handler);
  }, [selectedBusiness]);
  const planOpenPaths = new Set(['/billing', '/settings', '/support', '/pricing']);

  useEffect(() => {
    const handler = (event) => {
      const updatedId = Number(event.detail?.businessId);
      if (!updatedId || !selectedBusiness || updatedId === Number(selectedBusiness)) {
        refreshSelectedBusinessLogo();
      }
    };
    window.addEventListener('business-branding-updated', handler);
    return () => window.removeEventListener('business-branding-updated', handler);
  }, [selectedBusiness, refreshSelectedBusinessLogo]);

  const appLogoSrc = assetUrl(appConfig?.logo_url);

  return (
    <>
      <style>{logoAnimationStyles}</style>
      <div className="min-h-screen flex bg-gray-50">
        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" 
            onClick={() => setMobileMenuOpen(false)}
          ></div>
        )}

        {/* Sidebar - Desktop & Mobile */}
        <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col h-screen shrink-0 fixed inset-y-0 left-0 lg:sticky lg:top-0 z-50 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <div className="p-4 flex items-center justify-between border-b border-gray-700">
            {sidebarOpen ? (
              <Link to="/dashboard" className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity">
                {appLogoSrc ? (
                  <img 
                    src={appLogoSrc}
                    alt={appName}
                    style={{
                      width: appConfig.logo_width || 200,
                      height: appConfig.logo_height || 60,
                      objectFit: 'contain'
                    }}
                    className="max-w-full logo-spin-horizontal"
                  />
                ) : (
                  <>
                    <img 
                      src="/img/eloNestsymbol.png" 
                      alt={appName}
                      className="h-8 w-8 logo-spin-horizontal"
                      onError={(e) => {
                        // If logo doesn't exist, hide the image and show text
                        e.target.style.display = 'none';
                      }}
                    />
                    <h1 className="text-xl font-bold">{appName}</h1>
                  </>
                )}
              </Link>
            ) : (
              <Link to="/dashboard" className="cursor-pointer hover:opacity-80 transition-opacity">
                {appLogoSrc ? (
                  <img 
                    src={appLogoSrc}
                    alt={appName}
                    style={{
                      width: appConfig.logo_width || 200,
                      height: appConfig.logo_height || 60,
                      objectFit: 'contain'
                    }}
                    className="h-8 w-8 mx-auto logo-spin-horizontal"
                  />
                ) : (
                  <img 
                    src="/img/eloNestsymbol.png" 
                    alt={appName}
                    className="h-8 w-8 mx-auto logo-spin-horizontal"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </Link>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-300 hover:text-white hidden lg:block" aria-label="Toggle sidebar">
              {sidebarOpen ? '‹' : '›'}
            </button>
            {/* Mobile close button */}
            <button onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white lg:hidden ml-auto" aria-label="Close menu">✕</button>
          </div>

        {/* Business selector */}
        {sidebarOpen && (
          <div className="p-4 border-b border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <label className="text-xs text-gray-400 uppercase">Business</label>
            </div>
            {locationsError ? (
              <p className="text-sm text-red-400">Locations failed to load</p>
            ) : !locationsLoaded ? (
              <p className="text-sm text-gray-400">Loading locations…</p>
            ) : (
            <BusinessMultiSelect userId={user?.id} locations={switcherLocations} selectedBusiness={selectedBusiness} setSelectedBusiness={setSelectedBusiness} labelFor={getBusinessOptionLabel} />
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex min-h-0 flex-1 flex-col p-4">
          <div className="sidebar-nav-scroll min-h-0 flex-1 space-y-1 overflow-y-auto">
          {navigationSections.map((section) => (
            <div key={section.title}>
              {sidebarOpen && <div className="text-xs text-gray-500 uppercase font-semibold px-4 py-2 mt-4 first:mt-0">{section.title}</div>}
              {section.items.map((item) => (
                <Link 
                  key={item.path} 
                  to={item.path} 
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex w-full items-center ${sidebarOpen ? 'px-4' : 'justify-center flex-col'} py-2.5 rounded-lg transition-colors ${location.pathname === item.path ? 'text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'}`}
                  style={location.pathname === item.path ? {
                    backgroundColor: appConfig?.primary_color || '#3B82F6'
                  } : {}} 
                  title={!sidebarOpen ? item.name : ''}
                >
                  {item.icon && <span className={`text-lg ${sidebarOpen ? 'mr-0' : 'mb-1'}`}>{item.icon}</span>}
                  {sidebarOpen ? (
                    <span className="ml-3 text-sm">{item.name}</span>
                  ) : (
                    <span className="text-xs text-center leading-tight px-1">{item.name}</span>
                  )}
                </Link>
              ))}
            </div>
          ))}
          </div>
          {user && (
            <div ref={accountMenuRef} className="relative mt-2 shrink-0 border-t border-gray-700 pt-2">
              <button
                type="button"
                aria-label="Account"
                aria-expanded={accountMenuOpen}
                onClick={() => setAccountMenuOpen((open) => !open)}
                className={`flex w-full items-center rounded-lg transition-colors ${sidebarOpen ? 'px-4' : 'justify-center flex-col'} py-2.5 ${accountMenuOpen ? 'text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'}`}
                style={accountMenuOpen ? { backgroundColor: appConfig?.primary_color || '#3B82F6' } : {}}
              >
                <span className={`inline-flex h-7 w-7 items-center justify-center text-lg ${sidebarOpen ? 'mr-0' : 'mb-1'}`}>
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-700 text-xs font-medium leading-none text-white">{accountInitial}</span>
                </span>
                {sidebarOpen ? (
                  <span className="ml-3 truncate text-sm">{accountName}</span>
                ) : (
                  <span className="text-xs text-center leading-tight px-1">{accountName}</span>
                )}
              </button>
              {accountMenuOpen && (
                <div className="absolute bottom-full left-0 z-20 mb-2 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  {(!allowedPages || allowedPages.has('/billing')) && <Link to="/billing" onClick={() => { setAccountMenuOpen(false); setMobileMenuOpen(false); }} className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100">eloNest plan</Link>}
                  <Link to="/settings" onClick={() => { setAccountMenuOpen(false); setMobileMenuOpen(false); }} className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100">Settings</Link>
                  <div className="my-1 border-t border-gray-200" />
                  <button type="button" onClick={logout} className="block w-full px-4 py-2 text-left text-sm text-gray-800 hover:bg-gray-100">Logout</button>
                </div>
              )}
            </div>
          )}
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden w-full lg:w-auto">
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 lg:px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Hamburger menu button for mobile */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden text-gray-700 hover:text-gray-900 mr-4"
                aria-label="Open menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 min-w-0">
                  {selectedBusinessLogo && (
                    <img
                      src={selectedBusinessLogo}
                      alt="Business Logo"
                      onError={() => setSelectedBusinessLogo(null)}
                      className="h-10 w-10 rounded-md border border-gray-200 object-contain bg-white"
                    />
                  )}
                  <div className="min-w-0">
                    <SelectedBusinessesLabel locations={switcherLocations} selectedBusiness={selectedBusiness} color={appConfig?.primary_color} />
                    <h2 className="text-sm lg:text-base font-medium text-gray-500 truncate">{currentTitle}</h2>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 lg:space-x-4">
                {/* Quick Menu Items */}
                <div className="hidden lg:flex items-center space-x-2">
                  {quickMenuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                        location.pathname === item.path
                          ? 'text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      style={location.pathname === item.path ? {
                        backgroundColor: appConfig?.primary_color || '#3B82F6'
                      } : {}}
                    >
                      <span>{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
                {process.env.NODE_ENV !== 'production' && (
                  <>
                    <span className="text-sm text-gray-400 hidden lg:inline">|</span>
                    {/* Server Status - local dev only, checks the local backend process */}
                    <div className="hidden lg:flex items-center space-x-2">
                      {serverStatus === 'online' && <div className="flex items-center space-x-1 text-green-700"><div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div><span className="text-xs font-semibold">Server Online</span></div>}
                      {serverStatus === 'offline' && (
                        <div className="flex items-center space-x-2 text-red-700">
                          <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                          {user && (user.is_superuser || user.role === 'admin') ? (
                            <button onClick={requestRestart} className="text-xs font-semibold underline underline-offset-2">Server Offline</button>
                          ) : (
                            <span className="text-xs font-semibold">Server Offline</span>
                          )}
                          {user && (user.is_superuser || user.role === 'admin') && (
                            <button onClick={requestRestart} className="ml-2 px-2 py-0.5 text-xs border border-red-300 text-red-700 rounded hover:bg-red-50">Restart</button>
                          )}
                        </div>
                      )}
                      {serverStatus === 'checking' && <div className="flex items-center space-x-1 text-yellow-700"><div className="w-2.5 h-2.5 bg-yellow-500 rounded-full animate-pulse"></div><span className="text-xs font-semibold">Checking...</span></div>}
                    </div>
                  </>
                )}
                <span className="text-sm text-gray-400 hidden lg:inline">|</span>
                <span className="text-xs lg:text-sm text-gray-600 hidden sm:inline">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                {user && (
                  <div className="flex items-center space-x-2 lg:space-x-3">
                    <span className="text-xs lg:text-sm text-gray-700 hidden sm:inline">{user.first_name} {user.last_name}</span>
                    <button onClick={logout} className="px-2 lg:px-3 py-1 text-xs lg:text-sm bg-red-600 text-white rounded hover:bg-red-700">Logout</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">{pageBlocked ? (
          <p data-role-hidden="true" className="text-gray-700">This page is not part of your role here. <Link to="/dashboard" className="text-blue-600 underline">Back to the Dashboard</Link></p>
        ) : isPersonalLocation && personalHiddenPaths.has(location.pathname) && ['/customers', '/inventory', '/quotes'].includes(location.pathname) ? (
          <p className="text-gray-700">This page is not available for a household location.</p>
        ) : locationsLoaded && businesses.length === 0 && !user?.is_superuser && location.pathname !== '/dashboard' && location.pathname !== '/settings' && location.pathname !== '/billing' ? (
          <p data-empty-start="true" className="text-gray-800">Add a person or business on the Dashboard.</p>
        ) : planLock && !planOpenPaths.has(location.pathname) ? (
          <div data-plan-required="true" className="max-w-lg mx-auto mt-12 bg-white rounded-lg shadow p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {planLock === 'trial_ended' ? 'Your free trial has ended' : planLock === 'cancelled' ? 'This plan has ended' : 'Choose a plan for this location'}
            </h2>
            <p className="mt-3 text-gray-700">
              {getSelectedBusinessName()} needs an active plan before its books open. Your data is kept and opens again as soon as a plan starts.
            </p>
            <Link to="/billing" className="mt-6 inline-block px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              Go to Billing
            </Link>
          </div>
        ) : formedWaiting && formedClosedPaths.has(location.pathname) ? (
          <p className="text-gray-700">These screens stay closed until the formation paper, EIN letter, and owner ID photo are stored.</p>
        ) : React.isValidElement(children)
          ? <>{formationLock.inGrace && formedClosedPaths.has(location.pathname) && (
              <div data-formation-grace="true" className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                {graceMessage(formationLock.daysLeft)}{' '}
                <Link to="/business" className="font-medium underline">Upload on the Business page</Link>
              </div>
            )}{React.cloneElement(children, { key: `sel-${multiKey}`,
              selectedBusiness,
              setSelectedBusiness,
              divisionScopeIds: isDivisionScopedUser ? selectedDivisionScope : null,
            })}</>
          : children}
          <PageGuide />
        </main>

        {/* Footer with Disclaimer */}
        <footer className="bg-gray-50 border-t border-gray-200 mt-auto">
          <div className="px-4 lg:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">{appName}</h3>
                  {appConfig?.app_tagline && (
                    <p className="text-xs text-gray-600">{appConfig.app_tagline}</p>
                  )}
                  {appConfig?.support_email && (
                    <p className="text-xs text-gray-600 mt-2">
                      Support: <a href={`mailto:${appConfig.support_email}`} className="text-blue-600 hover:underline">{appConfig.support_email}</a>
                    </p>
                  )}
                  {appConfig?.support_phone && (
                    <p className="text-xs text-gray-600">
                      Phone: <a href={`tel:${appConfig.support_phone}`} className="text-blue-600 hover:underline">{appConfig.support_phone}</a>
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Legal</h3>
                  {appConfig?.terms_url && (
                    <p className="text-xs mb-1">
                      <a href={appConfig.terms_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a>
                    </p>
                  )}
                  {appConfig?.privacy_url && (
                    <p className="text-xs">
                      <a href={appConfig.privacy_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Quick Links</h3>
                  <div className="space-y-1">
                    <Link to="/dashboard" className="block text-xs text-blue-600 hover:underline">Dashboard</Link>
                    <Link to="/transactions" className="block text-xs text-blue-600 hover:underline">Transactions</Link>
                    <Link to="/reports" className="block text-xs text-blue-600 hover:underline">Reports</Link>
                  </div>
                </div>
              </div>
              
              {/* Financial Liability Disclaimer */}
              <div className="border-t border-gray-300 pt-4 mt-4">
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong className="text-gray-900">Important Disclaimer:</strong> eloNest provides financial management tools for informational purposes only. 
                  All financial data, calculations, and projections are provided "as is" without warranty of any kind. 
                  <strong className="text-gray-900"> You are solely responsible for verifying and validating all financial information displayed in this application.</strong> 
                  We strongly recommend consulting with a qualified accountant, financial advisor, or tax professional before making any financial decisions. 
                  The application developers, owners, and operators assume no liability for any errors, omissions, or inaccuracies in the financial data, 
                  nor for any financial losses or damages resulting from the use of this application. 
                  By using this application, you acknowledge that you have read, understood, and agree to take full responsibility for all financial decisions 
                  and actions taken based on information provided by this system.
                </p>
                {appConfig?.footer_text && (
                  <p className="text-xs text-gray-600 mt-3">{appConfig.footer_text}</p>
                )}
                <p className="text-xs text-gray-500 mt-3">
                  © {new Date().getFullYear()} {appName}. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Add Location Modal: Personal or Business first, then the optional documents step */}
      {showBusinessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            {checklistStep ? (
              <div data-setup-step="true" className="space-y-4">
                <h2 className="text-2xl font-bold">A few quick questions</h2>
                <p className="text-sm text-gray-700">
                  Tell us a little about the business and we'll make a simple checklist of what it needs. You can change these later on the Business page.
                </p>
                {(() => {
                  const next = () => {
                    const created = checklistStep;
                    setChecklistStep(null);
                    if (formationLockState(created).formed) {
                      setDocumentsStep(created);
                    } else {
                      setShowBusinessModal(false);
                      setLocationKind('');
                    }
                  };
                  return <SetupQuestions businessId={checklistStep.id} onDone={next} onSkip={next} saveLabel="Continue" />;
                })()}
              </div>
            ) : documentsStep ? (
              <div data-formation-step="true" className="space-y-4">
                <h2 className="text-2xl font-bold">Business documents</h2>
                <p className="text-sm text-gray-700">
                  Upload the formation paper, EIN letter, and owner ID photo now, or skip and add them later on the Business page.
                  {formationLockState(documentsStep).inGrace && (
                    <> You have {formationLockState(documentsStep).daysLeft} days before invoicing, payroll, and resolutions close until they are stored.</>
                  )}
                </p>
                <form
                  className="space-y-3"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    const data = new FormData(event.currentTarget);
                    setDocumentsSaving(true);
                    setDocumentsError('');
                    try {
                      for (const kind of ['formation_paper', 'ein_letter', 'owner_id_photo']) {
                        const file = data.get(`${kind}_file`);
                        const documentDate = data.get(`${kind}_date`);
                        if (file && file.name && documentDate) {
                          await businessesAPI.saveFormationDocument(documentsStep.id, kind, documentDate, file);
                        }
                      }
                      await loadBusinesses();
                      setDocumentsStep(null);
                      setShowBusinessModal(false);
                      setLocationKind('');
                    } catch (err) {
                      setDocumentsError(formatApiError(err, 'Could not save the documents'));
                    } finally {
                      setDocumentsSaving(false);
                    }
                  }}
                >
                  {[
                    ['formation_paper', 'Formation paper'],
                    ['ein_letter', 'IRS EIN letter (CP 575 or 147C)'],
                    ['owner_id_photo', 'Owner ID photo']
                  ].map(([kind, label]) => (
                    <div key={kind}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                      {kind === 'ein_letter' && (
                        <p className="text-xs text-gray-500 mb-1">The IRS letter that shows your EIN: CP 575 when it was assigned, or 147C if you asked the IRS to confirm it.</p>
                      )}
                      <div className="flex gap-2">
                        <input type="date" name={`${kind}_date`} className="border border-gray-300 rounded px-2 py-1 text-sm" aria-label={`${label} date`} />
                        <input type="file" name={`${kind}_file`} className="text-sm" aria-label={`${label} file`} />
                      </div>
                    </div>
                  ))}
                  {documentsError && <p className="text-sm text-red-700">{documentsError}</p>}
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => { setDocumentsStep(null); setShowBusinessModal(false); setLocationKind(''); }}
                      className="px-4 py-2 border rounded hover:bg-gray-50"
                    >
                      Skip for now
                    </button>
                    <button type="submit" disabled={documentsSaving} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                      {documentsSaving ? 'Saving...' : 'Save documents'}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
            <>
            <h2 className="text-2xl font-bold mb-4">Add New Location</h2>
            <div className="grid grid-cols-2 gap-3 mb-4" role="radiogroup" aria-label="Location type">
              {[
                ['personal', 'Personal', 'Household money. No business paperwork.'],
                ['business', 'Business', 'Legal name, tax ID, and business documents.']
              ].map(([value, label, hint]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={locationKind === value}
                  data-location-kind={value}
                  onClick={() => setLocationKind(value)}
                  className={`text-left border rounded p-3 ${locationKind === value ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <div className="font-semibold">{label}</div>
                  <div className="text-xs text-gray-600">{hint}</div>
                </button>
              ))}
            </div>
            {locationKind && (
            <form onSubmit={handleCreateBusiness} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{locationKind === 'personal' ? 'Name *' : 'Business Name *'}</label>
                <input type="text" required value={businessFormData.name} onChange={(e)=>setBusinessFormData({ ...businessFormData, name: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2" placeholder={locationKind === 'personal' ? 'e.g., Household' : 'e.g., ABC Consulting LLC'} />
              </div>
              {locationKind === 'business' && (
              <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name</label>
                <input type="text" value={businessFormData.legal_name} onChange={(e)=>setBusinessFormData({ ...businessFormData, legal_name: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2" placeholder="Full legal business name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID / EIN</label>
                <input type="text" value={businessFormData.tax_id} onChange={(e)=>setBusinessFormData({ ...businessFormData, tax_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2" placeholder="XX-XXXXXXX" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Business (optional)</label>
                <select
                  value={businessFormData.parent_business_id}
                  onChange={(e)=>setBusinessFormData({ ...businessFormData, parent_business_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">None - top-level / parent business</option>
                  {businesses.map((b) => (
                    <option key={b.id} value={b.id}>{getBusinessOptionLabel(b)}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose a parent to create a child business under that umbrella. The child is still a full business with its own EIN.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Legal Structure</label>
                <select value={businessFormData.business_type} onChange={(e)=>setBusinessFormData({ ...businessFormData, business_type: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2">
                  <option value="Sole Proprietorship">Sole Proprietorship</option>
                  <option value="LLC">LLC</option>
                  <option value="Corporation">Corporation</option>
                  <option value="S-Corp">S-Corp</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Non-Profit">Non-Profit</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows={3} value={businessFormData.description} onChange={(e)=>setBusinessFormData({ ...businessFormData, description: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2" />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={()=>{ setShowBusinessModal(false); setLocationKind(''); }} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Create</button>
              </div>
            </form>
            )}
            {!locationKind && (
              <div className="flex justify-end">
                <button type="button" onClick={()=>setShowBusinessModal(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
              </div>
            )}
            </>
            )}
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default Layout;


